import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Zap, ArrowRight, Satellite, Truck, Users, TrendingUp, Globe, Shield } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function Landing() {
  const { user } = useAuth();

  const features = [
    { icon: Truck, title: "CYBERTRUCK", desc: "Promote the best accessories and modifications for Tesla's revolutionary truck", color: "#E31937" },
    { icon: Satellite, title: "STARLINK", desc: "Share amazing Starlink applications from off-grid living to mobile offices", color: "#00BFFF" },
    { icon: Users, title: "COMMUNITY", desc: "Join a growing network of Tesla enthusiasts and content creators", color: "#22C55E" },
    { icon: TrendingUp, title: "EARNINGS", desc: "Track your affiliate performance with real-time analytics and stats", color: "#F59E0B" },
    { icon: Globe, title: "INFLUENCE", desc: "Build your presence in the Tesla community with curated content", color: "#A855F7" },
    { icon: Shield, title: "TRUSTED", desc: "Enterprise-grade affiliate tracking and management you can rely on", color: "#00BFFF" },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0C' }}>
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1716304960614-67625112f271?w=1600')" }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            className="max-w-3xl"
          >
            <motion.div custom={0} variants={fadeUp} className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-[#E31937] flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">CTCSB AFFILIATE PLATFORM</span>
            </motion.div>
            <motion.h1
              custom={1}
              variants={fadeUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-black leading-none uppercase tracking-tighter text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              POWER YOUR<br />
              <span className="text-[#E31937]">TESLA</span> IMPACT
            </motion.h1>
            <motion.p custom={2} variants={fadeUp} className="text-base md:text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              Join the premier affiliate network for Cybertruck accessories, Tesla products, and Starlink applications. Earn while growing the community.
            </motion.p>
            <motion.div custom={3} variants={fadeUp} className="flex flex-wrap gap-4">
              <Link
                to={user ? "/dashboard" : "/register"}
                data-testid="hero-cta-button"
                className="inline-flex items-center gap-2 bg-[#E31937] text-white px-8 py-4 font-bold uppercase tracking-wide hover:bg-[#FF3B30] transition-colors"
              >
                {user ? "Go to Dashboard" : "Get Started"}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/blog"
                data-testid="hero-learn-more"
                className="inline-flex items-center gap-2 border border-[#2A2A30] text-white px-8 py-4 font-bold uppercase tracking-wide hover:border-white hover:bg-white/5 transition-colors"
              >
                Explore Content
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 md:px-12 lg:px-24 py-24" style={{ background: '#0A0A0C' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase text-[#E31937] font-semibold">WHY CTCSB</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need to<br />Grow Your Influence
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#2A2A30]">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#141417] border border-[#2A2A30] p-8 hover:bg-[#1C1C20] transition-all duration-300 group"
                data-testid={`feature-card-${f.title.toLowerCase()}`}
              >
                <f.icon className="w-8 h-8 mb-4 transition-colors duration-300" style={{ color: f.color }} />
                <h3 className="text-lg font-bold text-white mb-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-[#2A2A30]" style={{ background: '#141417' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Join the <span className="text-[#00BFFF]">Movement</span>?
          </h2>
          <p className="text-base text-slate-400 mb-10 max-w-2xl mx-auto">
            Whether you're a Cybertruck owner, Starlink power user, or Tesla enthusiast — there's a place for you here. Start earning today.
          </p>
          <Link
            to="/register"
            data-testid="cta-join-button"
            className="inline-flex items-center gap-2 bg-[#E31937] text-white px-10 py-4 font-bold uppercase tracking-wide hover:bg-[#FF3B30] transition-colors text-lg"
          >
            Create Your Account
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2A2A30] px-6 md:px-12 lg:px-24 py-8" style={{ background: '#0A0A0C' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#E31937] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-slate-500">CTCSB &copy; {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-6 text-sm text-slate-500">
            <span>ctcsb.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
